import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DRAWER_HISTORY_PAGE_SIZE,
  type DrawerCloseResultDto,
  type DrawerCurrentDto,
  type DrawerMovementKind,
  type DrawerMovementResultDto,
  type DrawerPreviewDto,
  type DrawerSessionDto,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/** How often the open session's live figure is re-read. */
export const LIVE_REFRESH_MS = 30_000;

/**
 * Re-read on the live tick until `paused`. A close holds it still: the count
 * is judged against the figure the server answered for it, and a figure that
 * moves under the cashier mid-count is what `drawer_stale` refuses anyway.
 */
function useLiveRefresh(load: () => Promise<void>, paused: boolean): void {
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => void load(), LIVE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, paused]);
}

function failureMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError && caught.message ? caught.message : fallback;
}

/**
 * GET /cash-drawer/current — whether the venue's one drawer is in use, its
 * OPEN session, and the live expected figure (the owner and `reports.view`
 * only; null for everyone else, who count blind).
 *
 * `current` is undefined until the first answer, and keeps the last answer
 * when a refresh fails (which sets `error`): a screen must never decide "not
 * in use" — and offer the box screen or the owner's start — on a read that
 * has not landed.
 */
export function useDrawerCurrent(paused = false) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<DrawerCurrentDto | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setCurrent(await api.get<DrawerCurrentDto>('/cash-drawer/current'));
      setError(null);
    } catch (caught) {
      setError(failureMessage(caught, t('cashDrawer.loadFailed')));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);
  useLiveRefresh(load, paused);

  return { current, error, reload: load };
}

/**
 * Which screen answers /box-balance (switch-over, 2026-10-12): the box screen
 * until the owner starts the drawer — the main cashier still does the normal
 * counted box reset that day — and the drawer from then on.
 *
 * Unknown after a failed read: the box screen. It is the screen the address
 * names, and once the drawer is in use the server refuses every box reset
 * itself (`box_retired`), so a wrong guess costs a refusal, never money.
 */
export function boxBalanceDestination(
  current: DrawerCurrentDto | undefined,
  error: string | null,
): 'loading' | 'drawer' | 'box' {
  if (current?.enabled) return 'drawer';
  if (current === undefined && error === null) return 'loading';
  return 'box';
}

/**
 * One page of GET /cash-drawer/sessions: closed sessions only, newest first,
 * with the figures stored at their close.
 */
export interface DrawerSessionsPage {
  rows: DrawerSessionDto[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/** The history's route for one page — exported so the paging is pinned by tests. */
export function drawerSessionsPath(page: number): string {
  return `/cash-drawer/sessions?page=${page}`;
}

/**
 * The drawer history — the owner and `finance.manageShifts` only, so
 * `enabled` is false for everyone else and nothing is asked for. A failed
 * load keeps an ERROR, never an empty list: a history that silently reads
 * "no sessions" is a claim about money nobody verified.
 *
 * The API answers a bare array of one page; a full page means there may be
 * older sessions behind it.
 */
export function useDrawerSessions(enabled: boolean, page: number) {
  const { t } = useTranslation();
  const [data, setData] = useState<DrawerSessionsPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const rows = await api.get<DrawerSessionDto[]>(drawerSessionsPath(page));
      setData({
        rows,
        page,
        pageSize: DRAWER_HISTORY_PAGE_SIZE,
        hasMore: rows.length === DRAWER_HISTORY_PAGE_SIZE,
      });
      setError(null);
    } catch (caught) {
      setError(failureMessage(caught, t('cashDrawer.history.loadFailed')));
    }
  }, [enabled, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, reload: load };
}

/**
 * Step one of a close: the count alone. The server answers the expected cash
 * and the difference and saves nothing (owner, 2026-10-12: count first).
 */
export function previewClose(countedCashIqd: number): Promise<DrawerPreviewDto> {
  return api.post<DrawerPreviewDto>('/cash-drawer/close/preview', { countedCashIqd });
}

/** `closeDrawerSchema`'s body, as this screen sends it. */
export interface CloseDrawerBody {
  countedCashIqd: number;
  keptFloatIqd: number;
  note?: string;
  expectedCashMdShown: string;
  sessionId: string;
}

/**
 * The close request. The expected figure the preview ANSWERED rides along,
 * and so does the id of the session being closed: the server refuses
 * (`drawer_stale`) if cash moved since, or if that session is already
 * closed, instead of storing a difference against a figure nobody counted to
 * — or closing the next session with a count that belonged to this one.
 */
export function closeDrawerBody(
  input: { countedCashIqd: number; keptFloatIqd: number; note: string },
  expectedCashMdShown: string,
  sessionId: string,
): CloseDrawerBody {
  const note = input.note.trim();
  return {
    countedCashIqd: input.countedCashIqd,
    keptFloatIqd: input.keptFloatIqd,
    ...(note ? { note } : {}),
    expectedCashMdShown,
    sessionId,
  };
}

export function closeDrawer(
  input: { countedCashIqd: number; keptFloatIqd: number; note: string },
  expectedCashMdShown: string,
  sessionId: string,
): Promise<DrawerCloseResultDto> {
  return api.post<DrawerCloseResultDto>(
    '/cash-drawer/close',
    closeDrawerBody(input, expectedCashMdShown, sessionId),
  );
}

/**
 * Whole dinars typed into a count field, or null when nothing countable has
 * been entered. "0" is a real count of an empty drawer, not the absence of
 * one. Capped at a billion: a bound on a typo, not on a drawer.
 */
export function parseWholeIqd(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value <= 1_000_000_000 ? value : null;
}

/**
 * What goes to the safe at close: counted − kept, as millidinars. Null when
 * the float kept is more than was counted — the server refuses that too
 * (`drawer_kept_exceeds_counted`); this keeps the button from offering it.
 */
export function toSafeMd(countedIqd: number, keptIqd: number): string | null {
  if (keptIqd > countedIqd) return null;
  return ((BigInt(countedIqd) - BigInt(keptIqd)) * 1000n).toString();
}

/* -------------------------------------------------------------------------- */
/* The close, step by step                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Where a close stands. `count`: the cashier types what they counted, with
 * nothing on screen to agree with. `review`: the server's answer for that
 * count — expected and difference — with the note, the float kept and the
 * confirm.
 *
 * `recounted` is the owner's ONE recount (2026-10-12): offered once a
 * difference has been seen, and gone after it was used.
 */
export type CloseFlow =
  | { step: 'count'; recounted: boolean }
  | { step: 'review'; countedIqd: number; preview: DrawerPreviewDto; recounted: boolean };

export type CloseFlowEvent =
  | { type: 'previewed'; countedIqd: number; preview: DrawerPreviewDto }
  | { type: 'recount' }
  | { type: 'stale' };

export const START_CLOSE: CloseFlow = { step: 'count', recounted: false };

export function closeFlow(state: CloseFlow, event: CloseFlowEvent): CloseFlow {
  switch (event.type) {
    case 'previewed':
      return {
        step: 'review',
        countedIqd: event.countedIqd,
        preview: event.preview,
        recounted: state.recounted,
      };
    case 'recount':
      return canRecount(state) ? { step: 'count', recounted: true } : state;
    case 'stale':
      // Cash moved under the count, so the drawer holds different notes now:
      // a fresh close, its recount included — the cashier did nothing wrong.
      return START_CLOSE;
  }
}

/** The one recount: after a difference was seen, and only once. */
export function canRecount(state: CloseFlow): boolean {
  return state.step === 'review' && !state.recounted && BigInt(state.preview.varianceMd) !== 0n;
}

/** A difference needs a note (`drawer_note_required`); a match does not. */
export function closeNoteRequired(state: CloseFlow): boolean {
  return state.step === 'review' && BigInt(state.preview.varianceMd) !== 0n;
}

/* -------------------------------------------------------------------------- */
/* Movements and the owner's start                                            */
/* -------------------------------------------------------------------------- */

/**
 * `drawerMovementSchema`'s body. The amount is ALWAYS positive; the kind says
 * which way it goes, so a client bug can never turn a pickup into a top-up by
 * flipping a sign.
 */
export interface DrawerMovementBody {
  kind: DrawerMovementKind;
  amountIqd: number;
  note: string;
}

export function drawerMovementBody(
  kind: DrawerMovementKind,
  amountIqd: number,
  note: string,
): DrawerMovementBody {
  return { kind, amountIqd, note: note.trim() };
}

/**
 * A mid-session movement (owner and `safe.manage`), each a numbered safe
 * slip stamped to the open session: PICKUP drawer → safe, TOP_UP safe →
 * drawer, OWNER_TAKE drawer → owner (a drop and a withdrawal in one step).
 */
export function recordDrawerMovement(body: DrawerMovementBody): Promise<DrawerMovementResultDto> {
  return api.post<DrawerMovementResultDto>('/cash-drawer/movements', body);
}

/** `startCashDrawerSchema`'s body. */
export interface StartCashDrawerBody {
  countedSafeIqd: number;
  countedFloatIqd: number;
  note?: string;
}

export function startCashDrawerBody(input: {
  countedSafeIqd: number;
  countedFloatIqd: number;
  note: string;
}): StartCashDrawerBody {
  const note = input.note.trim();
  return {
    countedSafeIqd: input.countedSafeIqd,
    countedFloatIqd: input.countedFloatIqd,
    ...(note ? { note } : {}),
  };
}

/**
 * The owner's one-way switch-over (POST /cash-drawer/start). Its answer is
 * not read: the page re-reads the drawer, which is the confirmation.
 */
export function startCashDrawer(body: StartCashDrawerBody): Promise<unknown> {
  return api.post<unknown>('/cash-drawer/start', body);
}

/**
 * The correcting slip the start writes: the counted safe − the safe on the
 * books, as millidinars. Signed, and negative is the expected case: the old
 * figure added customer-balance and FIB money that were never notes.
 */
export function startAdjustmentMd(countedSafeIqd: number, balanceMd: string): string {
  return (BigInt(countedSafeIqd) * 1000n - BigInt(balanceMd)).toString();
}

/**
 * A box as GET /box-balance/employees answers it — only the parts the start
 * reads, mirrored here so the box screen's own types can retire without this.
 */
export interface OpenBoxRow {
  employeeId: string;
  employeeName: string;
  split: { cashMd: string };
}

/**
 * Boxes whose CASH is not zero — the ones worth a last counted reset before
 * the start. Non-cash never counts: the Portal box holds only balance spending.
 */
export function boxesHoldingCash(rows: readonly OpenBoxRow[]): OpenBoxRow[] {
  return rows.filter((row) => BigInt(row.split.cashMd) !== 0n);
}

/**
 * The boxes still holding cash, read once for the start dialog (the owner,
 * who holds reports.view). Undefined until the answer; a failure sets `error`
 * and never reads as "no box holds cash".
 */
export function useBoxesHoldingCash() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<OpenBoxRow[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get<{ rows: OpenBoxRow[] }>('/box-balance/employees')
      .then((overview) => {
        if (live) setRows(boxesHoldingCash(overview.rows));
      })
      .catch(() => {
        if (live) setError(t('cashDrawer.start.boxesFailed'));
      });
    return () => {
      live = false;
    };
  }, [t]);

  return { rows, error };
}
